<template>
  <div
    id="app"
    :class="{
      '-noMobileFooter': $route.meta.noMobileFooter,
      '-lessMobileTopPadding': $route.meta.noMobileMenus,
      '-noMobileBottomPadding': $route.meta.noMobileBottomPadding,
      '-lightBackground': hasLightBackground,
      '-hasChat': appSettings?.module_chat,
      '-fullContent': $route.meta.fullContent,
      '-askKeelearningBackground': $route.meta.askKeelearningBackground && hasLightBackground,
    }"
    class="o-app__wrapper">
    <!-- Skip link for accessibility, Lets keyboard users jump past the header/nav directly into the page content -->
    <a
      href="#main-content"
      class="c-skipLink">
      {{ $t('generic.skip_to_content') }}
    </a>
    <div class="o-app">
      <DefaultSidenav />
      <div class="o-mainContent__nextToSidenav">
        <div
          :style="wrapperStyle"
          class="o-mainContent__wrapper">
          <portal-target
            name="banner"
            slim />
          <!-- Main content anchor for skip link -->
          <main
            id="main-content"
            class="o-mainContent"
            :class="{ '-noSidebar': $route.meta.noSidebar }"
            data-cy="DefaultLayout__mainContent">
            <MobileHeader
              v-show="!$route.meta.noMobileMenus"
              :show-chat="$route.meta.fullContent && appSettings?.module_chat && !chatStore.isChatButtonHidden" />
            <OfflineIndicator />
            <router-view v-show="!isLoading" />
            <LoadingIndicator />
          </main>
          <MobileMenu v-show="!$route.meta.noMobileMenus && !$route.meta.noMobileFooter" />
          <nav
            v-if="isMobileSize"
            class="o-bottomNavigation"
            :aria-label="$t('nav.mobile_navigation')">
            <portal-target name="mobile-bottom" />
          </nav>
          <DefaultSidebar />
        </div>
        <portal-target name="footer" />
      </div>
    </div>
    <portal-target
      multiple
      name="modal" />
    <NotificationsList />
    <KeywordModal />
    <template v-if="appSettings?.module_chat">
      <ChatButton
        v-if="!chatStore.isChatButtonHidden && !$route.meta.heroChat"
        class="s-defaultLayout__floatingChat"
        :class="{
          '-chatOpened': chatStore.chatOpened,
          '-hiddenBelowDesktop': $route.meta.fullContent,
        }" />
      <transition name="bounce">
        <ChatDialog v-if="chatStore.chatOpened && appSettings.module_chat && !chatStore.isChatButtonHidden" />
      </transition>
    </template>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import OfflineIndicator from '@/components/OfflineIndicator.vue'
import DefaultSidebar from '@/layouts/partials/DefaultSidebar.vue'
import DefaultSidenav from '@/layouts/partials/DefaultSidenav.vue'
import MobileHeader from '@/layouts/partials/MobileHeader.vue'
import MobileMenu from '@/layouts/partials/MobileMenu.vue'
import NotificationsList from '@/layouts/partials/NotificationsList.vue'
import { useChatStore } from '@/modules/chat/chatStore'
import ChatButton from '@/modules/chat/components/ChatButton.vue'
import ChatDialog from '@/modules/chat/components/ChatDialog.vue'
import { removePushSubscriptions, subscribePushNotification } from '@/modules/global/logic/pushNotification'
import KeywordModal from '@/modules/keywords/components/KeywordModal.vue'
import store from '@/store'

export default {
  name: 'DefaultLayout',
  data() {
    return {
      chatStore: useChatStore(),
    }
  },
  mounted() {
    if (this.appSettings?.module_chat) {
      if (!window?.Capacitor && typeof Notification !== 'undefined') {
        if (Notification.permission === 'granted') {
          // Update the push notification token in case it has changed
          subscribePushNotification()
        } else {
          removePushSubscriptions()
        }
      }
    }

    if (window.Capacitor) {
      window.Capacitor.Plugins.SplashScreen.hide()
    }
  },
  computed: {
    appSettings() {
      return store.getters['auth/config']?.settings
    },
    isLoggedIn() {
      return store.getters['auth/isLoggedIn']
    },
    isLoading() {
      return store.getters.isLoading
    },
    screenSize() {
      return store.getters.screenSize
    },
    isMobileSize() {
      return this.screenSize.lt.tabletPortrait
    },
    config() {
      return store.getters['auth/config']
    },
    hasLightBackground() {
      return this.config.settings.tablet_light_background
    },
    wrapperStyle() {
      const style = {}
      if (this.$route.meta.contentBackgroundColor && window.innerWidth >= 1024) {
        style.background = this.$route.meta.contentBackgroundColor
      }
      return style
    },
  },
  components: {
    LoadingIndicator,
    OfflineIndicator,
    DefaultSidebar,
    DefaultSidenav,
    MobileHeader,
    MobileMenu,
    NotificationsList,
    ChatButton,
    ChatDialog,
    KeywordModal,
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/config';

@media screen and (max-width: #{$mq-tablet-landscape-size - 1}) {
  .s-defaultLayout__floatingChat.-hiddenBelowDesktop {
    display: none;
  }
}
</style>
